import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  IconButton,
  CardContent,
  Paper,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Warning as ToxicityIcon,
  Person as PersonIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useUserData } from '../../hooks/useUserData';
import { createToxicityRecord, getToxicityRecord, updateToxicityRecord } from '../../services/chemotherapyService';
import { searchPatients } from '../../services/patientService';
import {
  CTCAE_GRADES,
  TOXICITY_CATEGORIES,
  ADVERSE_EVENTS,
  CTCAE_GRADE_DESCRIPTIONS,
  CTCAE_GRADE_COLORS
} from '../../types/enums';
import { PageHeader, ContentCard } from './oncologyStyles';

const ToxicityAssessment = ({ patientId: propPatientId, chemoCycleId, treatmentPlanId, onSave, onCancel, embedded = false }) => {
  const { patientId: urlPatientId, recordId } = useParams();
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [patientOptions, setPatientOptions] = useState([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  
  const patientId = propPatientId || urlPatientId || selectedPatient?.id;
  const isEditing = !!recordId;
  
  const [loading, setLoading] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    adverseEvent: '',
    category: '',
    ctcaeGrade: '',
    onsetDate: new Date().toISOString().split('T')[0],
    resolvedDate: '',
    attribution: '',
    causativeAgent: '',
    interventions: [],
    outcomeDescription: '',
    treatmentDelayed: false,
    treatmentDiscontinued: false,
    doseReduced: false,
    hospitalizationRequired: false,
    notes: ''
  });

  // Load existing record if editing
  useEffect(() => {
    if (recordId && defaultFacility?.id) {
      const loadRecord = async () => {
        try {
          setLoadingRecord(true);
          const record = await getToxicityRecord(recordId);
          setFormData({
            adverseEvent: record.adverseEvent || '',
            category: record.category || '',
            ctcaeGrade: record.ctcaeGrade || '',
            onsetDate: record.onsetDate?.split('T')[0] || '',
            resolvedDate: record.resolvedDate?.split('T')[0] || '',
            attribution: record.attribution || '',
            causativeAgent: record.causativeAgent || '',
            interventions: record.interventions || [],
            outcomeDescription: record.outcomeDescription || '',
            treatmentDelayed: record.treatmentDelayed || false,
            treatmentDiscontinued: record.treatmentDiscontinued || false,
            doseReduced: record.doseReduced || false,
            hospitalizationRequired: record.hospitalizationRequired || false,
            notes: record.notes || ''
          });
          if (record.patientId) {
            setSelectedPatient({
              id: record.patientId,
              name: record.patientName || 'Unknown Patient',
              mrn: record.patientMRN || ''
            });
          }
        } catch (err) {
          console.error('Error loading toxicity record:', err);
          setError('Failed to load toxicity record');
        } finally {
          setLoadingRecord(false);
        }
      };
      loadRecord();
    }
  }, [recordId, defaultFacility?.id]);

  // Search patients
  const handlePatientSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2 || !defaultFacility?.id) {
      setPatientOptions([]);
      return;
    }

    try {
      setSearchingPatients(true);
      const results = await searchPatients({
        facilityId: defaultFacility.id,
        search: searchTerm,
        limit: 10
      });
      // Handle both array and object responses
      const patients = Array.isArray(results) ? results : (results?.patients || []);
      setPatientOptions(patients);
    } catch (err) {
      console.error('Error searching patients:', err);
      setPatientOptions([]);
    } finally {
      setSearchingPatients(false);
    }
  }, [defaultFacility?.id]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      handlePatientSearch(patientSearchTerm);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [patientSearchTerm, handlePatientSearch]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!patientId) {
      setError('Please select a patient');
      return;
    }

    if (!formData.adverseEvent || !formData.ctcaeGrade || !formData.category) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const toxicityData = {
        ...formData,
        patientId,
        patientName: selectedPatient?.name || selectedPatient?.firstName + ' ' + selectedPatient?.lastName,
        patientMRN: selectedPatient?.mrn,
        facilityId: defaultFacility.id,
        chemoCycleId,
        treatmentPlanId
      };

      let result;
      if (isEditing) {
        result = await updateToxicityRecord(recordId, toxicityData);
      } else {
        result = await createToxicityRecord(toxicityData);
      }
      
      setSuccess(true);

      if (onSave) {
        onSave(result);
      } else {
        // Navigate back to list after short delay
        setTimeout(() => {
          navigate('/oncology/toxicity');
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving toxicity record:', err);
      setError(err.message || 'Failed to save toxicity record');
    } finally {
      setLoading(false);
    }
  };

  const attributions = ['Definite', 'Probable', 'Possible', 'Unlikely', 'Unrelated'];
  const interventionOptions = [
    'Dose reduction',
    'Treatment delay',
    'Supportive medications',
    'IV fluids',
    'Blood transfusion',
    'G-CSF/Growth factors',
    'Antibiotics',
    'Anti-emetics',
    'Pain management',
    'Hospitalization',
    'ICU admission',
    'None required'
  ];

  const patientSelector = (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PersonIcon color="primary" />
        <Typography variant="subtitle1" fontWeight="bold">
          Patient Selection
        </Typography>
      </Box>
      
      {selectedPatient ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {selectedPatient.name || `${selectedPatient.firstName} ${selectedPatient.lastName}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              MRN: {selectedPatient.mrn || 'N/A'}
            </Typography>
          </Box>
          {!isEditing && (
            <Button size="small" onClick={() => setSelectedPatient(null)}>
              Change Patient
            </Button>
          )}
        </Box>
      ) : (
        <Autocomplete
          options={Array.isArray(patientOptions) ? patientOptions : []}
          getOptionLabel={(option) =>
            `${option.firstName || ''} ${option.lastName || ''} (MRN: ${option.mrn || 'N/A'})`
          }
          loading={searchingPatients}
          onInputChange={(e, value) => setPatientSearchTerm(value)}
          onChange={(e, value) => setSelectedPatient(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Patient"
              placeholder="Type patient name or MRN..."
              size="small"
              InputProps={{
                ...params.InputProps,
                startAdornment: <SearchIcon color="action" sx={{ ml: 1, mr: 0.5 }} />,
                endAdornment: (
                  <>
                    {searchingPatients ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box>
                <Typography variant="body2">
                  {option.firstName} {option.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MRN: {option.mrn || 'N/A'} | DOB: {option.dateOfBirth || 'N/A'}
                </Typography>
              </Box>
            </li>
          )}
          noOptionsText={patientSearchTerm.length < 2 ? "Type at least 2 characters" : "No patients found"}
        />
      )}
    </Paper>
  );

  const formContent = (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        {/* Adverse Event */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" required>
            <InputLabel>Adverse Event</InputLabel>
            <Select
              value={formData.adverseEvent}
              label="Adverse Event"
              onChange={(e) => handleChange('adverseEvent', e.target.value)}
            >
              {ADVERSE_EVENTS.map(event => (
                <MenuItem key={event} value={event}>{event}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Category */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" required>
            <InputLabel>Toxicity Category</InputLabel>
            <Select
              value={formData.category}
              label="Toxicity Category"
              onChange={(e) => handleChange('category', e.target.value)}
            >
              {TOXICITY_CATEGORIES.map(category => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* CTCAE Grade */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            CTCAE Grade *
          </Typography>
          <Grid container spacing={1}>
            {CTCAE_GRADES.filter(g => g !== '0').map(grade => (
              <Grid item xs={6} sm={4} md={2.4} key={grade}>
                <Button
                  fullWidth
                  variant={formData.ctcaeGrade === grade ? 'contained' : 'outlined'}
                  color={CTCAE_GRADE_COLORS[grade]}
                  onClick={() => handleChange('ctcaeGrade', grade)}
                  sx={{ 
                    py: 1.5,
                    flexDirection: 'column',
                    height: '100%'
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Grade {grade}
                  </Typography>
                  <Typography variant="caption" sx={{ textTransform: 'none', fontSize: '0.65rem' }}>
                    {CTCAE_GRADE_DESCRIPTIONS[grade]?.split(';')[0]}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Timing */}
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Onset Date"
            value={formData.onsetDate ? new Date(formData.onsetDate) : null}
            onChange={(date) => handleChange('onsetDate', date ? date.toISOString().split('T')[0] : '')}
            slotProps={{
              textField: { fullWidth: true, size: 'small', required: true }
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Resolved Date"
            value={formData.resolvedDate ? new Date(formData.resolvedDate) : null}
            onChange={(date) => handleChange('resolvedDate', date ? date.toISOString().split('T')[0] : '')}
            slotProps={{
              textField: { fullWidth: true, size: 'small' }
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Attribution</InputLabel>
            <Select
              value={formData.attribution}
              label="Attribution"
              onChange={(e) => handleChange('attribution', e.target.value)}
            >
              {attributions.map(attr => (
                <MenuItem key={attr} value={attr}>{attr}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Causative Agent */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Causative Agent (Drug Name)"
            value={formData.causativeAgent}
            onChange={(e) => handleChange('causativeAgent', e.target.value)}
            placeholder="e.g., Paclitaxel"
          />
        </Grid>

        {/* Interventions */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            multiple
            size="small"
            options={interventionOptions}
            value={formData.interventions}
            onChange={(e, newValue) => handleChange('interventions', newValue)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Interventions" placeholder="Select" />
            )}
          />
        </Grid>

        {/* Impact on Treatment */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Impact on Treatment
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formData.treatmentDelayed}
                  onChange={(e) => handleChange('treatmentDelayed', e.target.checked)}
                />
              }
              label="Treatment Delayed"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formData.doseReduced}
                  onChange={(e) => handleChange('doseReduced', e.target.checked)}
                />
              }
              label="Dose Reduced"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formData.treatmentDiscontinued}
                  onChange={(e) => handleChange('treatmentDiscontinued', e.target.checked)}
                />
              }
              label="Treatment Discontinued"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formData.hospitalizationRequired}
                  onChange={(e) => handleChange('hospitalizationRequired', e.target.checked)}
                />
              }
              label="Hospitalization Required"
            />
          </Box>
        </Grid>

        {/* Outcome */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Outcome Description"
            value={formData.outcomeDescription}
            onChange={(e) => handleChange('outcomeDescription', e.target.value)}
            placeholder="Describe the outcome of this adverse event..."
          />
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Additional Notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Any additional clinical notes..."
          />
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button 
          variant="outlined" 
          onClick={() => onCancel ? onCancel() : navigate('/oncology/toxicity')} 
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading || !patientId}
        >
          {loading ? 'Saving...' : (isEditing ? 'Update Record' : 'Save Toxicity Record')}
        </Button>
      </Box>
    </form>
  );

  // Loading state for edit mode
  if (loadingRecord) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Embedded mode
  if (embedded) {
    return (
      <ContentCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton onClick={() => onCancel ? onCancel() : navigate(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <ToxicityIcon color="error" />
            <Typography variant="h6" fontWeight="bold">
              Toxicity Assessment (CTCAE v5.0)
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Toxicity record saved successfully!
            </Alert>
          )}

          {!propPatientId && patientSelector}
          {formContent}
        </CardContent>
      </ContentCard>
    );
  }

  // Standalone page
  return (
    <Box>
      <PageHeader
        title={isEditing ? 'Edit Toxicity Record' : 'New Toxicity Record'}
        subtitle="CTCAE v5.0 adverse event documentation"
        icon={<ToxicityIcon />}
        backTo="/oncology/toxicity"
      />

      <ContentCard>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Toxicity record {isEditing ? 'updated' : 'saved'} successfully!
            </Alert>
          )}

          {!propPatientId && !urlPatientId && patientSelector}
          {formContent}
        </CardContent>
      </ContentCard>
    </Box>
  );
};

export default ToxicityAssessment;
